import { useMemo, useState, type ReactNode } from 'react';
import { useForecast, useForecastDates } from '../hooks/useForecast';
import type { ForecastCancelFlag, RefundForecastDate, RefundForecastItem } from '../types';

const usd = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const number = (n: number) => n.toLocaleString('en-US');

function formatHours(h: number): string {
  if (h <= 0) return '—';
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  const rem = h % 24;
  return rem ? `${d}d ${rem}h` : `${d}d`;
}

function formatDate(date: string): string {
  return new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function shortDate(date: string): string {
  return new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

const compactUsd = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;

// presale line items won't actually refund (excluded upstream from netRefund), so they
// are dropped from the at-risk SKU view entirely.
const isAtRisk = (it: RefundForecastItem) => it.cancelFlag !== 'excluded_presale';
const isReview = (f: ForecastCancelFlag | null) =>
  f === 'review_nonshippable' || f === 'review_stale';

interface SkuGroup {
  sku: string;
  items: RefundForecastItem[];
  orderCount: number;
  projectedRefund: number;
  unitsToCancel: number;
  reviewCount: number;
  worthCheckingCount: number;
  imsByWarehouse: { wh: string; q: number | null }[];
  analyzed: boolean;
}

type SortKey = 'refund' | 'orders' | 'units';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'refund', label: 'Projected $' },
  { value: 'orders', label: 'Orders' },
  { value: 'units', label: 'Units' },
];

const INITIAL_SKUS = 25;
const INITIAL_ORDERS = 8;

const FLAG_META: Record<ForecastCancelFlag, { label: string; cls: string }> = {
  excluded_presale: { label: 'Presale', cls: 'bg-gray-100 text-gray-600' },
  review_nonshippable: { label: 'Non-shippable', cls: 'bg-amber-100 text-amber-800' },
  review_stale: { label: 'Stale', cls: 'bg-amber-100 text-amber-800' },
};

function FlagBadge({ flag }: { flag: ForecastCancelFlag | null }) {
  if (!flag) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        Will cancel
      </span>
    );
  }
  const { label, cls } = FLAG_META[flag];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

// AI scoring/recommendations are a later phase — render an inert muted placeholder.
function AiPending({ className = '' }: { className?: string }) {
  return <span className={`text-gray-300 ${className}`}>—</span>;
}

type InventoryRec = NonNullable<RefundForecastItem['inventoryRecommendation']>;

const REC_META: Record<InventoryRec, { label: string; cls: string }> = {
  'check-chq': { label: 'Check CHQ', cls: 'bg-emerald-100 text-emerald-800' },
  borderline: { label: 'Borderline', cls: 'bg-amber-100 text-amber-800' },
  'likely-skip': { label: 'Skip', cls: 'bg-gray-100 text-gray-500' },
};

const worthChecking = (r: RefundForecastItem['inventoryRecommendation']) =>
  r === 'check-chq' || r === 'borderline';

function RecBadge({
  rec,
  confidence,
}: {
  rec: RefundForecastItem['inventoryRecommendation'];
  confidence?: RefundForecastItem['inventoryConfidence'];
}) {
  if (!rec) return <AiPending />;
  const { label, cls } = REC_META[rec];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
      title={confidence ? `${confidence} confidence` : undefined}
    >
      {label}
      {confidence && confidence !== 'high' && <span className="ml-1 opacity-60">· {confidence}</span>}
    </span>
  );
}

const WAREHOUSE_SHORT: Record<string, string> = {
  OMS_UPS_EAST: 'East',
  OMS_UPS_WEST: 'West',
  OMS_EU: 'EU',
};

const WAREHOUSE_ORDER = ['OMS_UPS_EAST', 'OMS_UPS_WEST', 'OMS_EU'];
const warehouseOrder = (wh: string) => {
  const i = WAREHOUSE_ORDER.indexOf(wh);
  return i === -1 ? WAREHOUSE_ORDER.length : i;
};

// Mirrors the backend's assigned-DC mapping (first entry of FILL_LOCATION_TO_IMS).
const FILL_TO_WAREHOUSE: Record<string, string> = {
  'UPS East - Ecomm': 'OMS_UPS_EAST',
  'UPS West - Ecomm': 'OMS_UPS_WEST',
  'Bleckmann - Ecomm': 'OMS_EU',
};

function ImsOnHand({ item }: { item: RefundForecastItem }) {
  if (!item.imsSnapshotDate) return <AiPending />;
  const q = item.imsOnHandQty ?? null;
  const title =
    `IMS snapshot ${item.imsSnapshotDate} at ${item.imsWarehouse ?? item.defaultFillLocation}: ` +
    `${q === null ? 'not tracked (MISSING_IN_IMS)' : `${q} on-hand`}` +
    (item.imsOnHandQtyPrev != null ? ` · prior ${item.imsOnHandQtyPrev}` : '');

  if (!item.imsWarehouseAlt) {
    if (q === null) return <span className="text-gray-400" title={title}>n/a</span>;
    return (
      <span className={q > 0 ? 'text-emerald-700 font-medium' : 'text-gray-400'} title={title}>
        {number(q)}
      </span>
    );
  }

  // Both UPS DCs — assigned first, the other tagged as the reroute option.
  const assigned = FILL_TO_WAREHOUSE[item.defaultFillLocation?.trim() ?? ''];
  const dcs = [
    { wh: item.imsWarehouse ?? '', q },
    { wh: item.imsWarehouseAlt, q: item.imsOnHandQtyAlt ?? null },
  ];
  if (dcs[1].wh === assigned) dcs.reverse();
  const anyStock = dcs.some((d) => (d.q ?? 0) > 0);
  const text = dcs
    .map(
      (d) =>
        `${d.q === null ? 'n/a' : number(d.q)} ${WAREHOUSE_SHORT[d.wh] ?? d.wh}${
          d.wh !== assigned ? ' (reroute)' : ''
        }`
    )
    .join(' · ');
  return (
    <span className={anyStock ? 'text-emerald-700 font-medium' : 'text-gray-400'} title={title}>
      {text}
    </span>
  );
}

function Card({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'danger' | 'savable';
}) {
  const valueCls =
    tone === 'danger' ? 'text-red-600' : tone === 'savable' ? 'text-emerald-600' : 'text-gray-900';
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${valueCls}`}>{value}</p>
      <p className="mt-1 text-xs text-gray-400 truncate">{sub ?? ' '}</p>
    </div>
  );
}

const TREND_DAYS = 15;

// Daily at-risk trend over the trailing window. Fed by the date-picker query (one summary per
// stored day), so it is independent of the selected day and persists across date changes.
function TrendChart({
  dates,
  selectedDate,
  onSelect,
}: {
  dates: RefundForecastDate[];
  selectedDate: string | undefined;
  onSelect: (date: string) => void;
}) {
  // dates arrive most-recent-first; show the trailing window chronologically
  const points = useMemo(
    () =>
      dates
        .filter((d): d is RefundForecastDate & { netRefund: number } => typeof d.netRefund === 'number')
        .slice(0, TREND_DAYS)
        .reverse(),
    [dates],
  );
  if (points.length < 2) return null;

  const W = 960;
  const H = 190;
  const PAD_X = 24;
  const PAD_TOP = 30;
  const PAD_BOTTOM = 26;
  const innerH = H - PAD_TOP - PAD_BOTTOM;
  const max = Math.max(...points.map((p) => p.netRefund)) || 1;
  const step = (W - 2 * PAD_X) / points.length;
  const barW = Math.min(40, step * 0.6);
  const cx = (i: number) => PAD_X + step * (i + 0.5);
  const hOf = (v: number) => (v <= 0 ? 0 : Math.max((v / max) * innerH, 2));
  const baselineY = H - PAD_BOTTOM;

  const hasSplit = points.some((p) => p.worthCheckingRefund != null);

  // Rect with only the top corners rounded (the data end); the baseline edge stays square.
  const topRoundedBar = (xLeft: number, yTop: number, h: number) => {
    const r = Math.min(4, h / 2, barW / 2);
    return `M${xLeft},${yTop + h} V${yTop + r} Q${xLeft},${yTop} ${xLeft + r},${yTop} H${
      xLeft + barW - r
    } Q${xLeft + barW},${yTop} ${xLeft + barW},${yTop + r} V${yTop + h} Z`;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="flex items-baseline justify-between">
        <p className="text-sm text-gray-500">Total at risk — last {points.length} days</p>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {hasSplit && (
            <>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: '#059669' }} />
                Worth checking
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: '#3b82f6' }} />
                Other at risk
              </span>
            </>
          )}
          <span className="text-gray-400">click a bar to view that day</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-1 w-full" role="img" aria-label="Total at risk per day">
        <line x1={PAD_X} y1={baselineY} x2={W - PAD_X} y2={baselineY} stroke="rgb(229 231 235)" />
        {points.map((p, i) => {
          const selected = p.date === selectedDate;
          const worth = Math.min(p.worthCheckingRefund ?? 0, p.netRefund);
          const rest = p.netRefund - worth;
          const worthH = hOf(worth);
          const restH = hOf(rest);
          // 2px surface gap between stacked segments
          const gap = worthH > 0 && restH > 0 ? 2 : 0;
          const restY = baselineY - restH;
          const worthY = restY - gap - worthH;
          const stackTop = worthH > 0 ? worthY : restY;
          const xLeft = cx(i) - barW / 2;
          return (
            <g key={p.date} className="cursor-pointer" onClick={() => onSelect(p.date)}>
              {/* full-height invisible hit area so short bars are easy to click */}
              <rect x={cx(i) - step / 2} y={0} width={step} height={H} fill="transparent">
                <title>
                  {`${formatDate(p.date)} — ${usd(p.netRefund)} at risk${
                    p.orderCount != null ? ` across ${number(p.orderCount)} orders` : ''
                  }${
                    p.worthCheckingRefund != null
                      ? ` · ${usd(worth)} worth checking${
                          p.worthCheckingCount != null ? ` (${number(p.worthCheckingCount)} items)` : ''
                        }`
                      : ''
                  }`}
                </title>
              </rect>
              {restH > 0 && (
                <path
                  d={
                    worthH > 0
                      ? `M${xLeft},${restY} H${xLeft + barW} V${baselineY} H${xLeft} Z`
                      : topRoundedBar(xLeft, restY, restH)
                  }
                  fill={selected ? 'rgb(37 99 235)' : '#3b82f6'}
                />
              )}
              {worthH > 0 && (
                <path d={topRoundedBar(xLeft, worthY, worthH)} fill={selected ? '#047857' : '#059669'} />
              )}
              <text
                x={cx(i)}
                y={stackTop - 6}
                textAnchor="middle"
                className={`text-[11px] ${selected ? 'fill-gray-900 font-semibold' : 'fill-gray-500'}`}
              >
                {compactUsd(p.netRefund)}
              </text>
              <text
                x={cx(i)}
                y={H - 8}
                textAnchor="middle"
                className={`text-[11px] ${selected ? 'fill-blue-600 font-semibold' : 'fill-gray-400'}`}
              >
                {shortDate(p.date)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function SummaryCards({
  skuCount,
  ordersAtRisk,
  totalAtRisk,
  top,
  worthCheckingCount,
  worthCheckingRefund,
  analyzed,
}: {
  skuCount: number;
  ordersAtRisk: number;
  totalAtRisk: number;
  top: SkuGroup | undefined;
  worthCheckingCount: number;
  worthCheckingRefund: number;
  analyzed: boolean;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card
        label="SKUs flagged"
        value={number(skuCount)}
        sub={`${number(ordersAtRisk)} orders at risk`}
      />
      <Card
        label="Top SKU exposure"
        tone="danger"
        value={top ? usd(top.projectedRefund) : '—'}
        sub={top ? `${top.sku} · ${number(top.orderCount)} orders` : undefined}
      />
      <Card
        label="Worth checking in CHQ"
        tone="savable"
        value={
          analyzed ? (
            <>
              {number(worthCheckingCount)}
              <span className="mx-2 text-gray-300">·</span>
              {usd(worthCheckingRefund)}
            </>
          ) : (
            <AiPending className="text-2xl" />
          )
        }
        sub={analyzed ? 'items · exposure — IMS snapshot signal' : 'Awaiting IMS analysis'}
      />
      <Card
        label="Total at risk"
        value={usd(totalAtRisk)}
        sub={`across ${number(ordersAtRisk)} orders`}
      />
    </div>
  );
}

function SkuGroupRow({
  group,
  expanded,
  onToggle,
  ordersExpanded,
  onToggleOrders,
}: {
  group: SkuGroup;
  expanded: boolean;
  onToggle: () => void;
  ordersExpanded: boolean;
  onToggleOrders: () => void;
}) {
  const orders = ordersExpanded ? group.items : group.items.slice(0, INITIAL_ORDERS);

  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={onToggle}>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <span
              className={`text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
              aria-hidden
            >
              ▸
            </span>
            <div>
              <div className="text-sm font-medium text-gray-900">{group.sku}</div>
              <div className="text-xs text-gray-400">
                {number(group.unitsToCancel)} units · {number(group.items.length)} line items
              </div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{number(group.orderCount)}</td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
          {usd(group.projectedRefund)}
        </td>
        <td className="px-4 py-3 text-sm whitespace-nowrap">
          {group.imsByWarehouse.length === 0 || group.imsByWarehouse.every((w) => w.q === null) ? (
            group.analyzed ? (
              <span className="text-gray-400" title="SKU not tracked in IMS at its warehouses">
                n/a
              </span>
            ) : (
              <AiPending />
            )
          ) : (
            <span
              className={
                group.imsByWarehouse.some((w) => (w.q ?? 0) > 0)
                  ? 'text-emerald-700 font-medium'
                  : 'text-gray-400'
              }
              title="IMS snapshot on-hand per warehouse this SKU's orders can ship from"
            >
              {group.imsByWarehouse
                .map((w) =>
                  group.imsByWarehouse.length > 1
                    ? `${w.q === null ? 'n/a' : number(w.q)} ${WAREHOUSE_SHORT[w.wh] ?? w.wh}`
                    : w.q === null
                      ? 'n/a'
                      : `${number(w.q)} on-hand`
                )
                .join(' · ')}
            </span>
          )}
        </td>
        <td className="px-4 py-3 text-sm whitespace-nowrap">
          {group.worthCheckingCount > 0 ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"
              title="Line items worth manually checking in CHQ for current ATS"
            >
              Check {number(group.worthCheckingCount)}
            </span>
          ) : !group.analyzed ? (
            <AiPending />
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              Skip
            </span>
          )}
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          {group.reviewCount > 0 ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
              title="Line items needing manual review before cancel"
            >
              {number(group.reviewCount)} review
            </span>
          ) : (
            <span className="text-gray-300">—</span>
          )}
        </td>
      </tr>

      {expanded && (
        <tr className="bg-gray-50/60">
          <td colSpan={6} className="px-4 pb-4 pt-1">
            <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 mb-3 text-xs text-blue-800">
              IMS on-hand is a once-a-day <span className="font-medium">snapshot</span>, not real-time ATS —
              CHQ ATS is the source of truth for whether an order can ship. Recommendations only flag which
              items are worth a manual CHQ check; they never release or flip a backorder automatically.
            </div>

            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Refund</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staleness</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IMS on-hand</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recommendation</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((it, idx) => (
                    <tr key={`${it.shopifyOrderId}-${idx}`}>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {it.shopifyOrderId}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                        {number(it.quantityToCancel)}
                        {it.qty > 0 && <span className="text-gray-400"> / {number(it.qty)}</span>}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                        {usd(it.totalRefundAmount)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">
                        {it.defaultFillLocation || '—'}
                      </td>
                      <td
                        className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap"
                        title={`${formatHours(it.excessHoursOverAcceptedThreshold)} over accept threshold · ${formatHours(it.excessHoursOverModifiedThreshold)} over modified threshold`}
                      >
                        {formatHours(it.excessHoursOverAcceptedThreshold)}
                      </td>
                      <td className="px-3 py-2 text-sm whitespace-nowrap">
                        <ImsOnHand item={it} />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <FlagBadge flag={it.cancelFlag} />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <RecBadge rec={it.inventoryRecommendation} confidence={it.inventoryConfidence} />
                        {it.inventoryReason && (
                          <p className="mt-1 max-w-xs text-xs text-gray-500">{it.inventoryReason}</p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {group.items.length > INITIAL_ORDERS && (
              <div className="text-center pt-2">
                <span className="text-xs text-gray-400">
                  Showing {number(orders.length)} of {number(group.items.length)} orders
                </span>
                <button
                  type="button"
                  onClick={onToggleOrders}
                  className="ml-2 text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                  {ordersExpanded ? 'Show less' : 'Show all'}
                </button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function RefundForecast() {
  const { data: dates, isLoading: datesLoading } = useForecastDates();
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const effectiveDate = selectedDate ?? dates?.[0]?.date;

  const { data: forecast, isLoading, error } = useForecast(effectiveDate);

  const [search, setSearch] = useState('');
  const [worthCheckingOnly, setWorthCheckingOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('refund');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [ordersExpanded, setOrdersExpanded] = useState<Set<string>>(new Set());
  const [showAllSkus, setShowAllSkus] = useState(false);

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, key: string) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    setter(next);
  };

  const groups = useMemo<SkuGroup[]>(() => {
    const map = new Map<string, RefundForecastItem[]>();
    for (const it of forecast?.items ?? []) {
      if (!isAtRisk(it)) continue;
      const arr = map.get(it.sku);
      if (arr) arr.push(it);
      else map.set(it.sku, [it]);
    }
    const result: SkuGroup[] = [];
    for (const [sku, items] of map) {
      // Per-warehouse on-hand for this SKU (same SKU ⇒ same qty per warehouse across its orders).
      const whQty = new Map<string, number | null>();
      for (const i of items) {
        if (i.imsWarehouse) whQty.set(i.imsWarehouse, i.imsOnHandQty ?? null);
        if (i.imsWarehouseAlt) whQty.set(i.imsWarehouseAlt, i.imsOnHandQtyAlt ?? null);
      }
      result.push({
        sku,
        items,
        orderCount: new Set(items.map((i) => i.shopifyOrderId)).size,
        projectedRefund: items.reduce((s, i) => s + i.totalRefundAmount, 0),
        unitsToCancel: items.reduce((s, i) => s + i.quantityToCancel, 0),
        reviewCount: items.filter((i) => isReview(i.cancelFlag)).length,
        worthCheckingCount: items.filter((i) => worthChecking(i.inventoryRecommendation)).length,
        imsByWarehouse: [...whQty.entries()]
          .map(([wh, q]) => ({ wh, q }))
          .sort((a, b) => warehouseOrder(a.wh) - warehouseOrder(b.wh)),
        analyzed: items.some((i) => i.inventoryRecommendation != null),
      });
    }
    return result;
  }, [forecast]);

  const metrics = useMemo(() => {
    const ordersAtRisk = new Set<string>();
    let totalAtRisk = 0;
    let top: SkuGroup | undefined;
    let worthCheckingCount = 0;
    let worthCheckingRefund = 0;
    let analyzed = false;
    for (const g of groups) {
      totalAtRisk += g.projectedRefund;
      g.items.forEach((i) => {
        ordersAtRisk.add(i.shopifyOrderId);
        if (i.imsSnapshotDate) analyzed = true;
        if (worthChecking(i.inventoryRecommendation)) {
          worthCheckingCount += 1;
          worthCheckingRefund += i.totalRefundAmount;
        }
      });
      if (!top || g.projectedRefund > top.projectedRefund) top = g;
    }
    return {
      skuCount: groups.length,
      ordersAtRisk: ordersAtRisk.size,
      totalAtRisk,
      top,
      worthCheckingCount,
      worthCheckingRefund,
      analyzed,
    };
  }, [groups]);

  const visibleGroups = useMemo(() => {
    const s = search.trim().toLowerCase();
    let filtered = s
      ? groups.filter(
          (g) =>
            g.sku.toLowerCase().includes(s) ||
            g.items.some((i) => i.shopifyOrderId.toLowerCase().includes(s)),
        )
      : groups;
    if (worthCheckingOnly) {
      filtered = filtered.filter((g) => g.worthCheckingCount > 0);
    }
    const val = (g: SkuGroup) =>
      sortKey === 'refund' ? g.projectedRefund : sortKey === 'orders' ? g.orderCount : g.unitsToCancel;
    return [...filtered].sort((a, b) => val(b) - val(a));
  }, [groups, search, sortKey, worthCheckingOnly]);

  const displayedGroups = showAllSkus ? visibleGroups : visibleGroups.slice(0, INITIAL_SKUS);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Refund Forecast</h1>
          <p className="mt-1 text-sm text-gray-500">
            {number(metrics.ordersAtRisk)} orders across {number(metrics.skuCount)} SKUs forecast to
            auto-cancel — grouped by SKU.
            {forecast?.generatedAt && (
              <span className="text-gray-400">
                {' '}Generated {new Date(forecast.generatedAt).toLocaleString('en-US')}.
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="forecast-date" className="text-sm text-gray-500">
            Date
          </label>
          <select
            id="forecast-date"
            value={effectiveDate ?? ''}
            onChange={(e) => setSelectedDate(e.target.value)}
            disabled={datesLoading || !dates || dates.length === 0}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
          >
            {dates?.map((d) => (
              <option key={d.date} value={d.date}>
                {formatDate(d.date)} ({d.lineItemCount})
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-red-600">
            Error loading forecast: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      )}

      {!error && !datesLoading && dates && dates.length === 0 && (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900">No forecasts available</h3>
          <p className="mt-1 text-sm text-gray-500">
            No refund forecast reports were found for the last 30 days.
          </p>
        </div>
      )}

      {dates && dates.length > 0 && (
        <TrendChart dates={dates} selectedDate={effectiveDate} onSelect={setSelectedDate} />
      )}

      {(isLoading || datesLoading) && (
        <div className="space-y-4 animate-pulse">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <div className="h-4 bg-gray-200 rounded w-24" />
                <div className="mt-3 h-7 bg-gray-200 rounded w-16" />
              </div>
            ))}
          </div>
          <div className="h-64 bg-white rounded-lg border border-gray-200" />
        </div>
      )}

      {!isLoading && !error && forecast && (
        <>
          <SummaryCards
            skuCount={metrics.skuCount}
            ordersAtRisk={metrics.ordersAtRisk}
            totalAtRisk={metrics.totalAtRisk}
            top={metrics.top}
            worthCheckingCount={metrics.worthCheckingCount}
            worthCheckingRefund={metrics.worthCheckingRefund}
            analyzed={metrics.analyzed}
          />

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search SKU…"
              className="w-full sm:w-80 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {metrics.analyzed && (
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={worthCheckingOnly}
                  onChange={(e) => setWorthCheckingOnly(e.target.checked)}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                Worth checking only
              </label>
            )}
            <div className="flex items-center gap-2 sm:ml-auto">
              <label htmlFor="forecast-sort" className="text-sm text-gray-500">
                Sort
              </label>
              <select
                id="forecast-sort"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label} ↓
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projected $</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IMS on-hand</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CHQ check</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayedGroups.map((g) => (
                    <SkuGroupRow
                      key={g.sku}
                      group={g}
                      expanded={expanded.has(g.sku)}
                      onToggle={() => toggle(expanded, setExpanded, g.sku)}
                      ordersExpanded={ordersExpanded.has(g.sku)}
                      onToggleOrders={() => toggle(ordersExpanded, setOrdersExpanded, g.sku)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {visibleGroups.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">No SKUs match the current search.</p>
              </div>
            )}

            {visibleGroups.length > 0 && (
              <div className="text-center py-3 border-t border-gray-100">
                <span className="text-sm text-gray-500">
                  Showing {number(displayedGroups.length)} of {number(visibleGroups.length)} SKUs
                </span>
                {visibleGroups.length > INITIAL_SKUS && (
                  <button
                    type="button"
                    onClick={() => setShowAllSkus((v) => !v)}
                    className="ml-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    {showAllSkus ? 'Show less' : 'Show all'}
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
